<script setup>
// NOTICE: KAPTION-INTEGRATION
// embedded-channel.DASHBOARD.3 / CONTINUITY.5 — Identity tab: identity mode,
// the identity secret (reveal / rotate), the legacy identifier_hash switch
// (embedded-channel.WEB.2) and the continuity mode: push_fallback_email (the
// default), push, email or both. The PATCH never sends null.
import { computed, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from 'vuex';
import { useAlert } from 'dashboard/composables';
import { KaptionEmbeddedAPI } from 'dashboard/api/kaptionEmbedded';
import SettingsSection from 'dashboard/components/SettingsSection.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';
import Switch from 'dashboard/components-next/switch/Switch.vue';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';

const props = defineProps({ inbox: { type: Object, required: true } });

const { t } = useI18n();
const store = useStore();
const accountId = computed(() => store.getters.getCurrentAccountId);

const channel = ref({ ...(props.inbox.embedded || {}) });
const saving = ref(false);
const secret = ref('');
const secretLoading = ref(false);
const rotating = ref(false);
const rotateDialog = ref(null);
const openDialog = ref(null);

const DEFAULT_CONTINUITY = 'push_fallback_email';

const identityModes = computed(() => [
  {
    value: 'verified',
    label: t('INBOX_MGMT.EMBEDDED.IDENTITY_MODES.VERIFIED'),
    help: t('INBOX_MGMT.EMBEDDED.IDENTITY_MODES.VERIFIED_HELP'),
  },
  {
    value: 'open',
    label: t('INBOX_MGMT.EMBEDDED.IDENTITY_MODES.OPEN'),
    help: t('INBOX_MGMT.EMBEDDED.IDENTITY_MODES.OPEN_HELP'),
  },
]);

const continuityOptions = computed(() => [
  {
    value: DEFAULT_CONTINUITY,
    label: t('INBOX_MGMT.EMBEDDED.IDENTITY.CONTINUITY_PUSH_FALLBACK_EMAIL'),
    help: t('INBOX_MGMT.EMBEDDED.IDENTITY.CONTINUITY_PUSH_FALLBACK_EMAIL_HELP'),
  },
  {
    value: 'push',
    label: t('INBOX_MGMT.EMBEDDED.IDENTITY.CONTINUITY_PUSH'),
    help: '',
  },
  {
    value: 'email',
    label: t('INBOX_MGMT.EMBEDDED.IDENTITY.CONTINUITY_EMAIL'),
    help: '',
  },
  {
    value: 'both',
    label: t('INBOX_MGMT.EMBEDDED.IDENTITY.CONTINUITY_BOTH'),
    help: '',
  },
]);

// A missing value is the server default, push with email fallback.
const selectedContinuity = computed(
  () => channel.value.continuity_mode || DEFAULT_CONTINUITY
);

const errorMessage = (error, fallback) => {
  const data = error?.response?.data;
  if (Array.isArray(data?.errors)) return data.errors.join(', ');
  return data?.error || data?.message || fallback;
};

const load = async () => {
  try {
    const { data } = await KaptionEmbeddedAPI.getChannel(
      accountId.value,
      props.inbox.id
    );
    channel.value = { ...channel.value, ...data };
  } catch (error) {
    useAlert(t('INBOX_MGMT.EMBEDDED.IDENTITY.LOAD_ERROR'));
  }
};

const save = async (changes, extra) => {
  const previous = channel.value;
  channel.value = { ...previous, ...changes };
  saving.value = true;
  try {
    const { data } = await KaptionEmbeddedAPI.updateChannel(
      accountId.value,
      props.inbox.id,
      changes,
      ...(extra ? [extra] : [])
    );
    channel.value = { ...channel.value, ...(data || {}) };
    useAlert(t('INBOX_MGMT.EMBEDDED.IDENTITY.SAVED'));
  } catch (error) {
    channel.value = previous;
    useAlert(errorMessage(error, t('INBOX_MGMT.EMBEDDED.IDENTITY.SAVE_ERROR')));
  } finally {
    saving.value = false;
  }
};

const legacyIdentifierHash = computed({
  get: () => !!channel.value.legacy_identifier_hash,
  set: value => {
    save({ legacy_identifier_hash: value });
  },
});

// rumo-sdk-backend.IDENTITY.6: turning verification off is confirmed first and
// sent with confirm: 'open'.
const setIdentityMode = (mode, event) => {
  if (mode === channel.value.identity_mode) return;
  if (mode === 'open') {
    // Keep the radio on the current mode until the admin confirms.
    if (event?.target) event.target.checked = false;
    openDialog.value?.open();
    return;
  }
  save({ identity_mode: mode });
};

const confirmOpen = async () => {
  openDialog.value?.close();
  await save({ identity_mode: 'open' }, { confirm: 'open' });
};

const setContinuity = mode => {
  if (mode === selectedContinuity.value) return;
  save({ continuity_mode: mode });
};

const revealSecret = async () => {
  secretLoading.value = true;
  try {
    const { data } = await KaptionEmbeddedAPI.revealSecret(
      accountId.value,
      props.inbox.id
    );
    secret.value = data.identity_secret;
  } catch (error) {
    useAlert(t('INBOX_MGMT.EMBEDDED.IDENTITY.SECRET_ERROR'));
  } finally {
    secretLoading.value = false;
  }
};

const hideSecret = () => {
  secret.value = '';
};

const askRotate = () => rotateDialog.value?.open();

const confirmRotate = async () => {
  rotating.value = true;
  try {
    const { data } = await KaptionEmbeddedAPI.rotateSecret(
      accountId.value,
      props.inbox.id
    );
    secret.value = data.identity_secret;
    useAlert(t('INBOX_MGMT.EMBEDDED.IDENTITY.ROTATED'));
  } catch (error) {
    useAlert(t('INBOX_MGMT.EMBEDDED.IDENTITY.SECRET_ERROR'));
  } finally {
    rotating.value = false;
    rotateDialog.value?.close();
  }
};

onMounted(load);
</script>

<template>
  <div class="mx-8">
    <SettingsSection
      :title="t('INBOX_MGMT.EMBEDDED.IDENTITY.MODE_TITLE')"
      :sub-title="t('INBOX_MGMT.EMBEDDED.IDENTITY.MODE_DESC')"
    >
      <div class="flex flex-col gap-2 max-w-xl" role="radiogroup">
        <label
          v-for="mode in identityModes"
          :key="mode.value"
          class="flex items-start gap-3 p-3 rounded-lg border cursor-pointer"
          :class="
            channel.identity_mode === mode.value
              ? 'border-n-brand bg-n-alpha-1'
              : 'border-n-weak'
          "
        >
          <input
            type="radio"
            name="embedded-identity-mode"
            class="mt-1"
            :value="mode.value"
            :checked="channel.identity_mode === mode.value"
            :disabled="saving"
            :data-testid="`identity-${mode.value}`"
            @change="setIdentityMode(mode.value, $event)"
          />
          <span class="flex flex-col">
            <span class="text-sm font-medium text-n-slate-12">
              {{ mode.label }}
            </span>
            <span class="text-sm text-n-slate-11">{{ mode.help }}</span>
          </span>
        </label>
      </div>
    </SettingsSection>

    <SettingsSection
      :title="t('INBOX_MGMT.EMBEDDED.IDENTITY.SECRET_TITLE')"
      :sub-title="t('INBOX_MGMT.EMBEDDED.IDENTITY.SECRET_DESC')"
    >
      <div class="flex flex-col gap-3 max-w-xl">
        <woot-code
          v-if="secret"
          :script="secret"
          lang="plaintext"
          data-testid="identity-secret"
        />
        <div class="flex flex-wrap gap-2">
          <NextButton
            v-if="!secret"
            outline
            slate
            :is-loading="secretLoading"
            :label="t('INBOX_MGMT.EMBEDDED.IDENTITY.REVEAL')"
            data-testid="reveal-secret"
            @click="revealSecret"
          />
          <NextButton
            v-else
            outline
            slate
            :label="t('INBOX_MGMT.EMBEDDED.IDENTITY.HIDE')"
            @click="hideSecret"
          />
          <NextButton
            outline
            ruby
            :label="t('INBOX_MGMT.EMBEDDED.IDENTITY.ROTATE')"
            data-testid="rotate-secret"
            @click="askRotate"
          />
        </div>
      </div>
    </SettingsSection>

    <SettingsSection
      :title="t('INBOX_MGMT.EMBEDDED.IDENTITY.LEGACY_TITLE')"
      :sub-title="t('INBOX_MGMT.EMBEDDED.IDENTITY.LEGACY_DESC')"
    >
      <div class="flex items-center gap-2">
        <Switch v-model="legacyIdentifierHash" data-testid="legacy-switch" />
        <span class="text-sm text-n-slate-12">
          {{ t('INBOX_MGMT.EMBEDDED.IDENTITY.LEGACY_TITLE') }}
        </span>
      </div>
    </SettingsSection>

    <SettingsSection
      :title="t('INBOX_MGMT.EMBEDDED.IDENTITY.CONTINUITY_TITLE')"
      :sub-title="t('INBOX_MGMT.EMBEDDED.IDENTITY.CONTINUITY_DESC')"
      :show-border="false"
    >
      <div class="flex flex-col gap-2 max-w-xl" role="radiogroup">
        <label
          v-for="option in continuityOptions"
          :key="option.value"
          class="flex items-start gap-3 p-3 rounded-lg border cursor-pointer"
          :class="
            selectedContinuity === option.value
              ? 'border-n-brand bg-n-alpha-1'
              : 'border-n-weak'
          "
        >
          <input
            type="radio"
            name="embedded-continuity-mode"
            class="mt-1"
            :value="option.value"
            :checked="selectedContinuity === option.value"
            :disabled="saving"
            :data-testid="`continuity-${option.value}`"
            @change="setContinuity(option.value)"
          />
          <span class="flex flex-col">
            <span class="text-sm font-medium text-n-slate-12">
              {{ option.label }}
            </span>
            <span v-if="option.help" class="text-sm text-n-slate-11">
              {{ option.help }}
            </span>
          </span>
        </label>
      </div>
    </SettingsSection>

    <Dialog
      ref="openDialog"
      type="alert"
      :title="t('INBOX_MGMT.EMBEDDED.IDENTITY.OPEN_CONFIRM_TITLE')"
      :description="t('INBOX_MGMT.EMBEDDED.IDENTITY.OPEN_CONFIRM_DESC')"
      :confirm-button-label="
        t('INBOX_MGMT.EMBEDDED.IDENTITY.OPEN_CONFIRM_BUTTON')
      "
      @confirm="confirmOpen"
    />
    <Dialog
      ref="rotateDialog"
      type="alert"
      :title="t('INBOX_MGMT.EMBEDDED.IDENTITY.ROTATE_CONFIRM_TITLE')"
      :description="t('INBOX_MGMT.EMBEDDED.IDENTITY.ROTATE_CONFIRM_DESC')"
      :confirm-button-label="t('INBOX_MGMT.EMBEDDED.IDENTITY.ROTATE')"
      :is-loading="rotating"
      @confirm="confirmRotate"
    />
  </div>
</template>
